import { useContext, useMemo, useState } from 'react'

import {
  SanityCaseOverviewModule,
  SanityTag,
} from '@data/sanity/queries/types/modules'
import { StringsContext } from '@lib/strings'

import Button, { ButtonColor, ButtonVariant } from '@components/buttons/button'
import CaseCard from '@components/case-card'

type CaseOverviewProps = Pick<
  SanityCaseOverviewModule,
  'title' | 'filterByTags' | 'cases'
>

const CaseOverview = ({ title, filterByTags, cases }: CaseOverviewProps) => {
  const strings = useContext(StringsContext)

  const [activeTag, setActiveTag] = useState('')

  const tags = useMemo(() => {
    const caseTags = cases.flatMap((caseItem) => {
      return caseItem.tags ?? []
    })

    // Filter unique tags by their values
    const caseTagValues = caseTags.map((tag) => tag.value)
    const uniqueTags = caseTags.filter(
      (value, index) => caseTagValues.indexOf(value.value) === index,
    )

    // Add "All" tag
    const tags: SanityTag[] = [
      {
        label: strings.buttonAllFilter,
        value: '',
      },
      ...uniqueTags,
    ]

    return tags
  }, [cases, strings])

  const filteredCases = useMemo(() => {
    return cases.filter((caseItem) => {
      if (activeTag === '') {
        return true
      }

      const caseTags = caseItem.tags ?? []
      const caseTagValues = caseTags.map((tag) => tag.value)

      return caseTagValues.includes(activeTag)
    })
  }, [cases, activeTag])

  return (
    <section className="container space-y-8 py-28">
      {!!title && <h1>{title}</h1>}

      {filterByTags && tags.length > 1 && (
        <div className="flex flex-wrap gap-5">
          {tags.map((tag) => (
            <Button
              key={tag.value}
              variant={
                activeTag === tag.value
                  ? ButtonVariant.FILLED
                  : ButtonVariant.OUTLINED
              }
              color={
                activeTag === tag.value
                  ? ButtonColor.GREEN_ELECTRIC
                  : ButtonColor.INHERIT
              }
              onClick={() => {
                setActiveTag(tag.value)
              }}
            >
              {tag.label}
            </Button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {filteredCases.map((caseItem) => (
          <CaseCard
            key={caseItem.slug}
            title={caseItem.title}
            slug={caseItem.slug}
            coverMediaType={caseItem.coverMediaType}
            coverImage={caseItem.coverImage}
            coverVideo={caseItem.coverVideo}
          />
        ))}
      </div>
    </section>
  )
}

export default CaseOverview
